import { useContext, useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { LocationOn } from '@mui/icons-material';
import { CityContext } from '../../ContextApi/cityContext';
import utilsCities from '../../_Utils/utilsCities';
import './CityPicker.css';

// עמוד מקום: /city/{slug} (עיר או מרחב). לזחלנים next-ssg מגיש גרסה משלו (next-ssg/pages/city/[slug].js).
// - בוחר את המקום לפי הכתובת (linked - בלי מסך בחירת העיר; הקישור גובר על עיר שנבחרה קודם).
// - slug לא קיים / מקום מושבת -> לדף הבית.
// - פס "מציגים מסעדות שמגיעות ל..." עם "החלף עיר", כדי שמי שהגיע מגוגל יידע איפה הוא.
// - בדיקת מיקום שקטה: רק אם הלקוח כבר אישר מיקום לאתר בעבר (navigator.permissions) - לעולם לא
//   מבקשים הרשאה מכאן. במקום אחר -> הצעה לעבור (לא עוברים לבד). בתוך המרחב -> מדייק את העיר בשקט.
// children - עמוד הבית, מוצג רק אחרי שהמקום נבחר (כדי שלא ייטען קודם עם המקום הקודם).

const HINT_KEY = 'amCityHintDone';

const geoGranted = async () => {
    try {
        if (!navigator.permissions || !navigator.permissions.query) return false;
        const st = await navigator.permissions.query({ name: 'geolocation' });
        return st && st.state === 'granted';
    } catch (e) {
        return false;
    }
};

const CityLanding = ({ children }) => {
    const { slug } = useParams();
    const history = useHistory();
    const { cities, ready, city, myCity, setCity, openPicker } = useContext(CityContext);
    const [hint, setHint] = useState(null); // { place, myCity } - נראה שהלקוח במקום אחר
    const applied = useRef(false); // המקום של הכתובת כבר נבחר

    const place = cities.find(p => p.slug === String(slug || '').toLowerCase()) || null;
    const matched = !!(place && city && String(city._id) === String(place._id));

    // בחירת המקום לפי הכתובת
    useEffect(() => {
        if (!ready) return;
        if (!place) { history.replace('/'); return; }
        if (!city || String(city._id) !== String(place._id)) setCity(place, null, { linked: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ready, place && place._id]);

    // הלקוח החליף מקום מתוך העמוד (בורר העיר) - הכתובת כבר לא מתאימה, עוברים לדף הבית.
    // applied נדלק רק אחרי שהמקום של הכתובת באמת נבחר (המקום הקודם לפני הבחירה לא נחשב החלפה)
    useEffect(() => {
        if (!place || !city) return;
        if (String(city._id) === String(place._id)) applied.current = true;
        else if (applied.current) history.replace('/');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [city && city._id]);

    // בדיקת מיקום שקטה - פעם אחת לכל מקום בביקור
    useEffect(() => {
        if (!matched) return;
        const key = `${HINT_KEY}_${place._id}`;
        try { if (sessionStorage.getItem(key)) return; sessionStorage.setItem(key, '1'); } catch (e) { }
        let alive = true;
        (async () => {
            if (!(await geoGranted())) return;
            try {
                const pos = await utilsCities.getPosition(8000);
                const res = await utilsCities.locate(pos.lat, pos.lng);
                if (!alive || !res || !res.city) return;
                if (String(res.city._id) === String(place._id)) {
                    // באותו מקום: במרחב - מדייקים את העיר בשקט
                    if (res.myCity && !myCity) setCity(place, res.myCity, { linked: true });
                    return;
                }
                setHint({ place: res.city, myCity: res.myCity || null });
            } catch (e) { }
        })();
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [matched]);

    if (!matched) return null;

    const label = myCity ? myCity.name : city.name;
    const hintName = hint ? (hint.myCity ? hint.myCity.name : hint.place.name) : '';

    const switchToHint = () => {
        setCity(hint.place, hint.myCity);
        setHint(null);
        history.replace('/');
    };

    return (
        <>
            <div className="cityLandBanner" dir="rtl">
                <LocationOn className="cityLandBannerIcon" />
                <span>מציגים מסעדות שמגיעות ל<b>{label}</b></span>
                <button type="button" className="cityLandBannerBtn" onClick={openPicker}>
                    לא ב{label}? החלף עיר
                </button>
            </div>

            {hint &&
                <div className="cityLandHint" dir="rtl" role="status">
                    <span>נראה שאתם ב<b>{hintName}</b></span>
                    <button type="button" className="cityLandHintGo" onClick={switchToHint}>עבור ל{hintName}</button>
                    <button type="button" className="cityLandHintStay" onClick={() => setHint(null)}>הישאר ב{label}</button>
                </div>}

            {children}
        </>
    );
};

export default CityLanding;
